import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  ToggleButtonGroup, ToggleButton
} from '@mui/material'
import hipersendService from '../../services/hipersend'
import Text from '../Text'

const sf = hipersendService.smartFilter

const ACTION_OPTIONS = [
  ['BLOCK', 'Descartar (não envia)'],
  ['REQUIRE_APPROVAL', 'Segurar para aprovação']
]

function isoToBr(s) {
  if (!s) return ''
  const [y, m, d] = String(s).slice(0, 10).split('-')
  return y && m && d ? `${d}/${m}/${y}` : s
}
function brl(n) {
  return Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// Extrai a config do SEND_BUDGET de uma regra (modo edição) ou devolve defaults.
function fromRule(rule) {
  const item = ((rule && rule.conditions && rule.conditions.items) || []).find((it) => it.type === 'SEND_BUDGET')
  const period = (item && item.period) || { type: 'monthly' }
  return {
    limit: item ? item.value : 10000,
    periodType: period.type === 'fixed' ? 'fixed' : 'monthly',
    from: period.from ? String(period.from).slice(0, 10) : '',
    to: period.to ? String(period.to).slice(0, 10) : '',
    action: (rule && rule.action && rule.action.type) || 'BLOCK',
    name: (rule && rule.name) || ''
  }
}

// Criar/editar um "teto de envios no período" (regra com condição SEND_BUDGET).
// Custo exibido ao cliente (variável de referência — nunca expor o unitário)
const CLIENT_COST_PER_MSG = 0.06

export default function BudgetDialog({ open, rule, costBRL, api, secret, onClose, onSaved, notify, usaLinhaDeCredito, precoMsgPacote }) {
  const editing = !!(rule && rule._id)
  const [limit, setLimit] = useState(10000)
  const [periodType, setPeriodType] = useState('monthly')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [action, setAction] = useState('BLOCK')
  const [name, setName] = useState('')
  const [activate, setActivate] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    const init = fromRule(rule)
    setLimit(init.limit)
    setPeriodType(init.periodType)
    setFrom(init.from)
    setTo(init.to)
    setAction(init.action)
    setName(init.name)
    setActivate(rule ? rule.enabled !== false : true)
  }, [open, rule])

  const autoName = periodType === 'fixed'
    ? `Teto de envios · ${isoToBr(from) || '?'} a ${isoToBr(to) || '?'}`
    : 'Teto de envios · mensal'

  const valid = Number(limit) > 0 && (periodType === 'monthly' || (from && to && from < to))

  const save = async () => {
    setSaving(true)
    try {
      const period = periodType === 'fixed' ? { type: 'fixed', from, to } : { type: 'monthly' }
      const payload = {
        name: (name || autoName).trim(),
        description: 'Teto de envios no período — trava o gasto ao atingir o limite.',
        source: 'any',
        priority: 1200,
        enabled: activate,
        conditions: { op: 'AND', items: [{ type: 'SEND_BUDGET', period, operator: 'gte', value: Number(limit) }] },
        action: { type: action, params: { reason: 'budget_cap_reached' } },
        templateCode: 'teto-envios-periodo'
      }
      if (periodType === 'fixed') { payload.validFrom = from; payload.validUntil = to }

      if (editing) await sf.updateRule(api, secret, rule._id, payload)
      else await sf.createRule(api, secret, payload)

      await onSaved()
      notify(editing ? 'Teto atualizado' : activate ? 'Teto criado e ativado' : 'Teto criado (desativado)')
      onClose()
    } catch (e) {
      notify('Falha ao salvar o teto')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, pb: 1 }}>
        <span style={{ fontSize: 20 }}>🎯</span> {editing ? 'Editar teto de envios' : 'Teto de envios no período'}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 3, py: 3 }}>
        <div className="space-y-5">
          <Text className="block text-sm opacity-70" style={{ lineHeight: 1.45 }}>
            Define um máximo de mensagens no período. Ao atingir, o filtro aplica a ação escolhida — um teto de gasto garantido.
          </Text>

          <TextField
            fullWidth size="small" type="number" label="Máximo de envios"
            value={limit} inputProps={{ min: 1 }}
            onChange={(e) => setLimit(e.target.value)}
            helperText={usaLinhaDeCredito && Number(limit) > 0 ? `≈ ${brl(Number(limit) * (precoMsgPacote || CLIENT_COST_PER_MSG))} de teto estimado` : ' '}
          />

          <div>
            <Text className="block text-xs uppercase tracking-wide opacity-60 mb-2" style={{ letterSpacing: '0.06em' }}>Período</Text>
            <ToggleButtonGroup size="small" exclusive fullWidth value={periodType} onChange={(e, v) => v && setPeriodType(v)}>
              <ToggleButton value="monthly" sx={{ textTransform: 'none' }}>Todo mês (reseta sozinho)</ToggleButton>
              <ToggleButton value="fixed" sx={{ textTransform: 'none' }}>Datas fixas</ToggleButton>
            </ToggleButtonGroup>
            {periodType === 'fixed' && (
              <div className="flex gap-2 mt-3">
                <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} fullWidth />
                <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} fullWidth />
              </div>
            )}
          </div>

          <FormControl fullWidth size="small">
            <InputLabel>Ao atingir o teto</InputLabel>
            <Select label="Ao atingir o teto" value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTION_OPTIONS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            fullWidth size="small" label="Nome da regra (opcional)"
            placeholder={autoName} value={name} onChange={(e) => setName(e.target.value)}
          />

          <FormControlLabel
            sx={{ ml: 0 }}
            control={<Switch checked={activate} onChange={(e) => setActivate(e.target.checked)} />}
            label={<span className="text-sm">Ativar agora</span>}
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={!valid || saving} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
          {saving ? 'Salvando…' : editing ? 'Salvar' : 'Criar teto'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
